import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheKeyConstants, isNullOrUndefined } from '@uzurv/core';
// eslint-disable-next-line import/order
import { AuthDataService, UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Banner, BannerSeverity, BannerType } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class BannerService extends UzurvRestService {
  readonly resourceUrl = 'banners/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private authDataService: AuthDataService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Gets all banners
   *
   * @param pageNumber
   * @param pageSize
   */
  listAllBanners(pageNumber: number, pageSize: number): Observable<Array<Banner>> {
    return this.get<Array<Banner>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => this.removeUserDismissedBanners(Deserialize(response, Banner))));
  }

  /**
   * List banners for an app. This is used by the crm
   *
   * @param appBundleIdentifier
   * @param appVersion
   * @param releasePlatformCode
   */
  listBannersByApp(appBundleIdentifier: string, appVersion: string, releasePlatformCode: string): Observable<Array<Banner>> {
    return this.get<Array<Banner>>(this.resourceUrl, {
      appBundleIdentifier: appBundleIdentifier,
      appVersion: appVersion,
      releasePlatformCode: releasePlatformCode
    }).pipe(map(response => this.removeUserDismissedBanners(Deserialize(response, Banner))));
  }

  /**
   * Gets all banner types
   *
   * @param pageNumber
   * @param pageSize
   */
  listAllBannerTypes(pageNumber: number, pageSize: number): Observable<Array<BannerType>> {
    return this.get<Array<BannerType>>(`${this.resourceUrl}bannerTypes/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, BannerType)));
  }

  /**
   * Gets all banner severities
   *
   * @param pageNumber
   * @param pageSize
   */
  listAllBannerSeverities(pageNumber: number, pageSize: number): Observable<Array<BannerSeverity>> {
    return this.get<Array<BannerSeverity>>(`${this.resourceUrl}bannerSeverities/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, BannerSeverity)));
  }

  /**
   * Finds a banner by id
   */
  findById(bannerId: number): Observable<Banner> {
    return this.get<Banner>(`${this.resourceUrl}${bannerId}`).pipe(map(response => Deserialize(response, Banner)));
  }

  /**
   * Creates a new banner
   *
   * @param banner
   * @permission banner_management
   */
  createBanner(banner: Banner): Observable<Banner> {
    return this.post<Banner>(this.resourceUrl, banner).pipe(map(response => Deserialize(response, Banner)));
  }

  /**
   * Updates an existing new banner
   *
   * @param banner
   * @permission banner_management
   */
  updateBanner(banner: Banner): Observable<Banner> {
    return this.put<Banner>(`${this.resourceUrl}${banner.id}`, banner).pipe(map(response => Deserialize(response, Banner)));
  }

  /**
   * Delete a banner
   *
   * @param bannerId
   */
  deleteBanner(bannerId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${bannerId}`);
  }

  /**
   * Updates the dismissed banners local storage
   *
   * @param banner
   */
  dismissBanner(banner: Banner) {
    // Dismiss banner for logged in user

    if (!this.authDataService.isLoggedIn) {
      return;
    }

    /*
      { userId: [bannerId1, bannerId2] }
    */

    /*
    * TODO: Refactor to not use type name as key name.
    */
    const dismissedBanners: { number: Array<number> } = isNullOrUndefined(localStorage.getItem(CacheKeyConstants.DISMISSED_BANNERS))
      ? {}
      : JSON.parse(localStorage.getItem(CacheKeyConstants.DISMISSED_BANNERS));

    if (isNullOrUndefined(dismissedBanners[this.authDataService.currentUser.claraPersonId])) {
      dismissedBanners[this.authDataService.currentUser.claraPersonId] = [];
    }

    // Don't dismiss the same banner twice
    if (dismissedBanners[this.authDataService.currentUser.claraPersonId].some(udb => banner.id === udb)) {
      return;
    }

    dismissedBanners[this.authDataService.currentUser.claraPersonId].push(banner.id);

    localStorage.setItem(CacheKeyConstants.DISMISSED_BANNERS, JSON.stringify(dismissedBanners));
  }

  /**
   * Filters out user dismissed banners
   *
   * @param banners
   */
  removeUserDismissedBanners(banners: Array<Banner>): Array<Banner> {
    if (!this.authDataService.isLoggedIn) {
      return banners;
    }

    if (isNullOrUndefined(localStorage.getItem(CacheKeyConstants.DISMISSED_BANNERS))) {
      return banners;
    }

    /*
    * TODO: Refactor to not use type name as key name.
    */
    const dismissedBanners: { number: Array<number> } = JSON.parse(localStorage.getItem(CacheKeyConstants.DISMISSED_BANNERS));

    if (isNullOrUndefined(dismissedBanners[this.authDataService.currentUser.claraPersonId])) {
      return banners;
    }

    const userDismissedBannerIds: Array<number> = dismissedBanners[this.authDataService.currentUser.claraPersonId];

    return banners.filter(b => !userDismissedBannerIds.some(udb => b.id === udb));
  }
}
